import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { customEvent } from "src/utils/gtag.js";
import UAParser from "ua-parser-js";
import {
  generateGCalReminder,
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
  useIsMounted,
} from "src/utils/Utils.js";
import { useMutation } from "@tanstack/react-query";
import InstructionsLayout from "../InstructionsLayout.js";
import { ProgressButton } from "src/components/blocks/ProgressButton.js";
import PersonalizeTags from "src/components/blocks/tags/PersonalizeTags.js";
import { useSubscribe } from "src/utils/useSubscribe.js";

const CalendarSubscriptionExperience = () => {
  const { user } = useContext(AuthContext);
  const uaParser = new UAParser();
  const parserResults = uaParser.getResult();
  const isAndroid = parserResults.os.name === "Android";
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const [initialLoading, setInitialLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const shouldDownloadReminder = isAndroid && user.provider === "google";
  const isMounted = useIsMounted();
  const mounted = isMounted();

  const navigate = useNavigate();

  useEffect(() => {
    if (!calendar) {
      navigate(getPageRoute("home", "HOME"));
    }
  }, [calendar, navigate]);

  const handleDownloadGCalReminder = async () => {
    customEvent({
      name: "user_click_download_gcal_reminder",
      category: "SubscribingCalendar",
      label: "Download Google Calendar Reminder Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    const stanzaLink = `${calendar?.stanzaLink}/${user?._id}.ics`;
    const icsContent = generateGCalReminder(calendar?.name, stanzaLink);
    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    // Create a temporary link to trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.download = `Sync_${calendar?.name}_GCal_Reminder.ics`;

    // Append to the document and trigger the click
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  };

  const handleOpenCalendar = () => {
    customEvent({
      name: "user_click_open_calendar",
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    return true;
  };

  const {
    mutateAsync: handleFreeSubscribing,
    data: isDoneMutation,
    isLoading,
  } = useMutation(
    shouldDownloadReminder ? handleDownloadGCalReminder : handleOpenCalendar
  );

  useEffect(() => {
    if (isDoneMutation) {
      setIsDone(true);
      setTimeout(() => {
        navigate(
          getPageRoute(
            "help",
            "SUBSCRIBING_CALENDAR_VERIFY",
            {
              handle: calendar.handle,
              calendarId: calendar._id,
            },
            { email: user.email, is_reminder: shouldDownloadReminder }
          ),
          {
            state: { calendar },
          }
        );
      }, 2000); // Give users time to see the pulsing button
    }
  }, [isDoneMutation, navigate, calendar]);

  // Simulate progress for the loading state
  useEffect(() => {
    if (initialLoading || isLoading) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prevProgress + 5;
        });
      }, 150);

      return () => clearInterval(interval);
    }
  }, [initialLoading, isLoading]);

  const { subscribe, subscriptionError, isLoadingSubscribing } = useSubscribe();

  useEffect(() => {
    if (!mounted) return;
    setTimeout(() => {
      setInitialLoading(false);
    }, 3000);

    // While ProgressButton loads user subscribe to calendar
    if (calendar) subscribe({ calendar });
  }, [mounted]);

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_experience",
      category: "CalendarSubscription",
      label: "Landed on Subscription Experience",
      data: {
        user_id: user?._id,
        user_email: user?.email,
      },
    });
  }, []);

  const buttonLoadingText = shouldDownloadReminder
    ? "Prepping your reminder"
    : "Prepping your subscription";
  const buttonText = !subscriptionError
    ? shouldDownloadReminder
      ? "Download reminder"
      : "Open calendar"
    : "Try again";

  const getFinalText = () => {
    if (shouldDownloadReminder) {
      return (
        <>
          <b>Don't forget to come back</b> to tell us that it worked so your
          reminder stays active.
        </>
      );
    }
    return (
      <>
        <b>Don't forget to come back</b> to tell us that it worked so your
        subscription stays active.
      </>
    );
  };

  return (
    <InstructionsLayout>
      <Box
        data-sticky="true"
        sx={{
          mt: -1,
          pt: 1,
        }}
      >
        <ProgressButton
          variant="contained"
          size="grown"
          sx={{ mt: 2, width: "100%" }}
          onClick={(event) => {
            event.preventDefault();
            if (!subscriptionError) {
              handleFreeSubscribing();
            } else {
              subscribe({ calendar }); // retry subscribing to calendar
            }
          }}
          loading={initialLoading || isLoading || isLoadingSubscribing}
          loadingText={initialLoading ? buttonLoadingText : null}
          progress={progress}
          isDone={isDone}
          finalText={getFinalText()}
        >
          {buttonText}
        </ProgressButton>
        <Divider sx={{ width: "100%", mt: 3 }} />
      </Box>

      <Box sx={{ placeItems: "flex-start", textAlign: "left", mt: 4 }}>
        <Typography variant={"title"}>While you're waiting...</Typography>
        <Typography
          variant={"subtitle2"}
          sx={{ mt: 2.5, mb: 5 }}
          color="text.secondary"
        >
          Choose tags that match your interests so we can help you find more of
          what you like.
        </Typography>
        <PersonalizeTags
          onTagChange={(action, tag) => {
            customEvent({
              name: `${action}_subscription_experience_tag`,
              category: "SubscribingCalendar",
              label: `User ${action} subscription experience tag`,
              data: {
                user_id: user._id,
                user_email: user.email,
                user_handle: user.handle ?? "no_handle",
                tag,
              },
            });
          }}
        />
      </Box>
    </InstructionsLayout>
  );
};

export default CalendarSubscriptionExperience;
